import {
  Alert,
  AlertIcon,
  Badge,
  Checkbox,
  Flex,
  HStack,
  Input,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import { NewbornPKURecord } from '@hb/shared'
import { useAuth } from '@hb/react'

interface Props {
  record: NewbornPKURecord
  onChange: (r: NewbornPKURecord) => void
  timeOfBirthMs: number | undefined
}

export const NewbornPKUSection = ({ record, onChange, timeOfBirthMs }: Props) => {
  const { authUser } = useAuth()
  const set = <K extends keyof NewbornPKURecord>(k: K, v: NewbornPKURecord[K]) =>
    onChange({ ...record, [k]: v })

  // Timing compliance: hours since birth
  const hoursSinceBirth = timeOfBirthMs
    ? Math.floor((Date.now() - timeOfBirthMs) / (1000 * 60 * 60))
    : null

  const screenNotDocumented48h =
    hoursSinceBirth != null && hoursSinceBirth >= 48 && !record.screenOrdered

  return (
    <VStack align="stretch" spacing={4}>
      {screenNotDocumented48h && (
        <Alert status="error" borderRadius={6} py={2} fontSize="sm">
          <AlertIcon />
          Newborn screen not documented — {hoursSinceBirth}h since birth. State mandate: screen
          required within 48 hours.
        </Alert>
      )}

      <Flex direction="column" gap={2}>
        <Checkbox
          isChecked={!!record.screenOrdered}
          onChange={e => {
            set('screenOrdered', e.target.checked)
            if (e.target.checked && !record.screenOrderedDate) {
              set('screenOrderedDate', new Date().toISOString().slice(0, 10))
            }
          }}
          colorScheme="pink"
          fontFamily="Hero-New"
          fontSize="sm">
          Newborn metabolic screen (PKU) ordered{' '}
          <Text as="span" color="red.400">
            *
          </Text>
        </Checkbox>
        {record.screenOrdered && (
          <Flex direction="column" gap={1} ml={6}>
            <Text fontFamily="Hero-New" fontSize="xs" color="gray.500">
              Screen order date
            </Text>
            <Input
              type="date"
              value={record.screenOrderedDate ?? ''}
              onChange={e => set('screenOrderedDate', e.target.value)}
              size="sm"
              fontFamily="Hero-New"
              maxW="200px"
            />
          </Flex>
        )}
      </Flex>

      {record.screenOrdered && (
        <>
          <Flex direction="column" gap={1}>
            <Text
              fontFamily="Hero-New"
              fontWeight={600}
              fontSize="sm"
              color="gray.700"
              _dark={{ color: 'gray.200' }}>
              Collection Date / Time{' '}
              <Text as="span" color="red.400">
                *
              </Text>
            </Text>
            <Input
              type="datetime-local"
              value={record.collectionDate ?? ''}
              onChange={e => set('collectionDate', e.target.value)}
              size="sm"
              fontFamily="Hero-New"
              maxW="260px"
            />
          </Flex>

          <Flex direction="column" gap={1}>
            <Text
              fontFamily="Hero-New"
              fontWeight={600}
              fontSize="sm"
              color="gray.700"
              _dark={{ color: 'gray.200' }}>
              Screen Kit ID / Card Number
            </Text>
            <Input
              value={record.screenKitId ?? ''}
              onChange={e => set('screenKitId', e.target.value)}
              size="sm"
              fontFamily="Hero-New"
              placeholder="Unique identifier on the newborn screening card"
              maxW="280px"
            />
          </Flex>

          <Flex direction="column" gap={1}>
            <Text
              fontFamily="Hero-New"
              fontWeight={600}
              fontSize="sm"
              color="gray.700"
              _dark={{ color: 'gray.200' }}>
              Collected By
            </Text>
            <Input
              value={record.collectedBy ?? authUser?.displayName ?? ''}
              onChange={e => set('collectedBy', e.target.value)}
              size="sm"
              fontFamily="Hero-New"
              maxW="280px"
            />
          </Flex>

          {hoursSinceBirth != null && (
            <HStack>
              <Badge
                colorScheme={hoursSinceBirth < 48 ? 'green' : 'red'}
                fontFamily="Hero-New"
                fontSize="xs">
                {hoursSinceBirth}h since birth
              </Badge>
              {hoursSinceBirth < 48 ? (
                <Text fontSize="xs" color="green.600" fontFamily="Hero-New">
                  Within 48-hour window
                </Text>
              ) : (
                <Text fontSize="xs" color="red.600" fontFamily="Hero-New">
                  Outside 48-hour mandate
                </Text>
              )}
            </HStack>
          )}

          <Flex direction="column" gap={2}>
            <Checkbox
              isChecked={!!record.resultsReceived}
              onChange={e => set('resultsReceived', e.target.checked)}
              colorScheme="pink"
              fontFamily="Hero-New"
              fontSize="sm">
              Results received
            </Checkbox>
            {record.resultsReceived && (
              <Flex direction="column" gap={2} ml={6}>
                <Flex direction="column" gap={1}>
                  <Text fontFamily="Hero-New" fontSize="xs" color="gray.500">
                    Results date
                  </Text>
                  <Input
                    type="date"
                    value={record.resultsDate ?? ''}
                    onChange={e => set('resultsDate', e.target.value)}
                    size="sm"
                    fontFamily="Hero-New"
                    maxW="200px"
                  />
                </Flex>
                <Checkbox
                  isChecked={!!record.resultsAbnormal}
                  onChange={e => set('resultsAbnormal', e.target.checked)}
                  colorScheme="red"
                  fontFamily="Hero-New"
                  fontSize="sm">
                  Abnormal screen result
                </Checkbox>
              </Flex>
            )}
          </Flex>

          {record.resultsAbnormal && (
            <>
              <Alert status="error" borderRadius={6} py={2} fontSize="sm">
                <AlertIcon />
                Abnormal newborn screen — follow-up required. Document follow-up plan before
                neonatal record can be closed.
              </Alert>
              <Flex direction="column" gap={1}>
                <Text
                  fontFamily="Hero-New"
                  fontWeight={600}
                  fontSize="sm"
                  color="gray.700"
                  _dark={{ color: 'gray.200' }}>
                  Follow-Up Plan{' '}
                  <Text as="span" color="red.400">
                    *
                  </Text>
                </Text>
                <Textarea
                  value={record.abnormalFollowUpPlan ?? ''}
                  onChange={e => set('abnormalFollowUpPlan', e.target.value)}
                  placeholder="Document referral, specialist contact, and follow-up timeline…"
                  size="sm"
                  fontFamily="Hero-New"
                  rows={3}
                />
              </Flex>
            </>
          )}
        </>
      )}
    </VStack>
  )
}
